// Browse Page - Filterable anime list
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  useMediaQuery,
  useTheme,
  Skeleton,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { FilterList, ExpandMore, KeyboardArrowDown } from '@mui/icons-material';
import { searchAnime, fetchByGenre, fetchTrending, getGenres, Anime } from '../../hooks/useApi';
import { AnimeCard } from '../../components/ui/Card';

const GENRE_LIST = getGenres();
const TYPES = ['TV', 'MOVIE', 'OVA', 'ONA', 'SPECIAL'];
const STATUSES = ['RELEASING', 'FINISHED', 'HIATUS', 'CANCELLED'];
const pageSize = 30;

export default function Browse() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = React.useState(!isMobile);

  const selectedGenre = searchParams.get('genre') || '';
  const selectedType = searchParams.get('type') || '';
  const selectedStatus = searchParams.get('status') || '';
  const selectedYear = searchParams.get('year') || '';
  const searchQuery = searchParams.get('q') || '';

  const [displayedItems, setDisplayedItems] = React.useState<Anime[]>([]);
  const [page, setPage] = React.useState(1);
  const [allItems, setAllItems] = React.useState<Anime[]>([]);

  // Fetch search or browse results
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['browse', searchQuery, selectedGenre, page],
    queryFn: async () => {
      if (searchQuery) return searchAnime(searchQuery);
      if (selectedGenre) return fetchByGenre(selectedGenre, page, pageSize);
      return fetchTrending(page, pageSize);
    },
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    if (!isLoading && data) {
      if (page === 1) setAllItems(data);
      else setAllItems(prev => [...prev, ...data]);
    }
  }, [data, page, isLoading]);

  React.useEffect(() => {
    if (allItems.length > 0) {
      const filtered = allItems.filter(a => {
        if (selectedType && a.type !== selectedType) return false;
        if (selectedStatus && a.status !== selectedStatus) return false;
        if (selectedYear && a.year?.toString() !== selectedYear) return false;
        return true;
      });
      setDisplayedItems(filtered);
    } else if (!isLoading) {
      setDisplayedItems(allItems);
    }
  }, [allItems, isLoading, selectedType, selectedStatus, selectedYear]);

  React.useEffect(() => { setPage(1); setAllItems([]); setDisplayedItems([]); },
    [searchQuery, selectedGenre, selectedType, selectedStatus, selectedYear]);

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (params.get(key) === value) params.delete(key);
    else params.set(key, value);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams(searchQuery ? { q: searchQuery } : {});
  };

  const activeFilterCount = [selectedGenre, selectedType, selectedStatus, selectedYear].filter(Boolean).length;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={900}>
          {searchQuery ? `Results for "${searchQuery}"` : selectedGenre ? `${selectedGenre} Anime` : 'Browse Anime'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isLoading ? 'Loading...' : `${displayedItems.length} anime found`}
        </Typography>
      </Box>

      <Box sx={{
        mb: 3,
        p: 2,
        borderRadius: 3,
        backgroundColor: 'var(--anime-token-surface-default)',
        border: '1px solid var(--anime-token-border-default)',
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: filtersOpen ? 2 : 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterList fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>Filters</Typography>
            {activeFilterCount > 0 && <Chip label={activeFilterCount} size="small" color="primary" />}
          </Stack>
          <Stack direction="row" spacing={1}>
            {activeFilterCount > 0 && (
              <Chip label="Clear All" size="small" variant="outlined" onClick={clearFilters} />
            )}
            <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)}>
              <ExpandMore sx={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
            </IconButton>
          </Stack>
        </Stack>

        {filtersOpen && (
          <Box sx={{ pt: 1 }}>
            <Stack direction="column" spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Genres</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {GENRE_LIST.map(g => (
                    <Chip key={g} label={g} size="small" clickable
                      variant={selectedGenre === g ? 'filled' : 'outlined'}
                      color={selectedGenre === g ? 'primary' : 'default'}
                      onClick={() => setFilter('genre', g)}
                    />
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Type</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {TYPES.map(t => (
                    <Chip key={t} label={t} size="small" clickable
                      variant={selectedType === t ? 'filled' : 'outlined'}
                      color={selectedType === t ? 'primary' : 'default'}
                      onClick={() => setFilter('type', t)}
                    />
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Status</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {STATUSES.map(s => (
                    <Chip key={s} label={s.charAt(0) + s.slice(1).toLowerCase()} size="small" clickable
                      variant={selectedStatus === s ? 'filled' : 'outlined'}
                      color={selectedStatus === s ? 'primary' : 'default'}
                      onClick={() => setFilter('status', s)}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 3 }}>
          {Array.from({ length: 24 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={300} sx={{ borderRadius: 3 }} />
          ))}
        </Box>
      ) : displayedItems.length > 0 ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 3 }}>
            {displayedItems.map((anime, index) => (
              <motion.div
                key={`${anime.id}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.6) }}
              >
                <AnimeCard
                  id={anime.id}
                  title={anime.title}
                  poster={anime.poster}
                  score={anime.score}
                  type={anime.type}
                  episodes={anime.episodes}
                  genres={anime.genres}
                  year={anime.year}
                  status={anime.status}
                  isAdult={anime.isAdult}
                  onClick={() => window.location.href = `/anime/${anime.id}`}
                />
              </motion.div>
            ))}
          </Box>

          {data && data.length >= pageSize && (
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Button
                variant="outlined"
                onClick={() => setPage(p => p + 1)}
                disabled={isFetching}
                sx={{ minWidth: 160 }}
              >
                {isFetching ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h5" color="text.tertiary">No anime found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your filters or search query
          </Typography>
        </Box>
      )}
    </Box>
  );
}